'use client';

import React, { useMemo, useState, useRef, useEffect } from 'react';

export type DropdownSelectOption<TValue extends string = string> = {
	value: TValue;
	label: string;
	disabled?: boolean;
};

export type DropdownSelectProps<TValue extends string = string> = {
	className?: string;
	options: ReadonlyArray<DropdownSelectOption<TValue>>;
	value?: TValue;
	onChange?: (value: TValue) => void;
	disabled?: boolean;
	iconSrc?: string;
	iconAlt?: string;
};

export default function DropdownSelect<TValue extends string = string>(props: DropdownSelectProps<TValue>) {
	const { className, options, value, onChange, disabled, iconSrc, iconAlt } = props;

	const [isOpen, setIsOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement | null>(null);

	const placeholderOption = options.find((o) => o.value === ('' as TValue));
	const placeholderLabel = placeholderOption?.label ?? '';

	const selectedLabel = useMemo(() => {
		const found = options.find((o) => o.value === value)?.label;
		return found ?? placeholderLabel ?? '';
	}, [options, value, placeholderLabel]);

	const isPlaceholder = !value || value === ('' as TValue);

	useEffect(() => {
		const onDocMouseDown = (e: MouseEvent) => {
			if (!rootRef.current) {
				return;
			}
			if (!rootRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', onDocMouseDown);
		return () => document.removeEventListener('mousedown', onDocMouseDown);
	}, []);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		requestAnimationFrame(() => {
			triggerRef.current?.scrollIntoView({
				block: 'start',
				inline: 'nearest',
				behavior: 'smooth',
			});
			if (menuRef.current) {
				menuRef.current.scrollTop = 0;
			}
		});
	}, [isOpen]);

	const handleSelect = (nextValue: TValue) => {
		if (disabled) {
			return;
		}
		onChange?.(nextValue);
		setIsOpen(false);
	};

	const menuOptions = options.filter((o) => o.value !== ('' as TValue));

	return (
		<div ref={rootRef} className={`DropdownSelect ${className ?? ''}`}>
			<button
				ref={triggerRef}
				type="button"
				className="dropdown-trigger flex ac js"
				onClick={() => !disabled && setIsOpen((prev) => !prev)}
				disabled={disabled}
			>
				{iconSrc ? (
					<img
						src={iconSrc}
						alt={iconAlt ?? ''}
						aria-hidden={iconAlt ? undefined : true}
						className="DropdownSelect__icon"
						width={16}
						height={16}
					/>
				) : null}

				<span className={`DropdownSelect__value ${isPlaceholder ? 'is-placeholder' : ''}`}>
					{selectedLabel}
				</span>
			</button>

			{isOpen ? (
				<div ref={menuRef} className="DropdownSelect__menu" role="listbox">
					{menuOptions.map((option) => (
						<button
							key={option.value}
							type="button"
							className={`DropdownSelect__option ${option.disabled ? 'is-disabled' : ''}`}
							onClick={() => !option.disabled && handleSelect(option.value)}
							disabled={option.disabled}
						>
							{option.label}
						</button>
					))}
				</div>
			) : null}
		</div>
	);
}
